<template>
  <div class="form-quizz-basic-info-component">
    <step-title
      title="Informações básicas"
      description="Configure a avaliação"
      icon="icon-info"
      class="py-5"
    />

    <form-group
      v-model="form.title"
      id="name"
      label="Nome da avaliação"
      placeholder="Ex: Avaliação final"
      :errors="errors.title"
      required
    />
    <div class="d-flex justify-content-end">
      <small
        class="text-secondary"
        :class="{
          'text-danger': form.title && form.title.length > 64
        }"
      >
        {{ `${form.title ? form.title.length : 0}/64` }}
      </small>
    </div>

    <form-group
      v-model="form.description"
      id="orientations"
      label="Orientações"
      placeholder="Explique de forma breve como funciona a avaliação e quanto tempo ela dura"
      textarea
      :errors="errors.description"
      rows="5"
      required
    />
    <div class="d-flex align-items-center mb-9">
      <small class="text-secondary flex-fill">
        Atenção! Esta é a descrição que exibiremos para os seus alunos antes de
        começar a avaliação.
      </small>
      <small
        class="text-secondary"
        :class="{
          'text-danger': form.description && form.description.length > 1000
        }"
      >
        {{ `${form.description ? form.description.length : 0}/1000` }}
      </small>
    </div>
    <div class="mb-6">
      <h6 class="indicator-title lg pb-0 mb-0">Configurações avançadas</h6>
      <small class="indicator-description xs">
        Estas configurações são opcionais, configure como preferir
      </small>
    </div>

    <div class="mb-6">
      <b-form-checkbox id="_days_to_release" v-model="time_limit_enable" switch>
        Determinar o tempo para realização da avaliação
      </b-form-checkbox>

      <div v-if="time_limit_enable">
        <form-group
          v-model="form.time_limit_minutes"
          :errors="errors.time_limit_minutes"
          id="time_limit_minutes"
          mask="###########"
          placeholder="00min"
          label="Tempo em minutos"
          :maxNumber="600"
          required
        />
      </div>
    </div>

    <div class="mb-6">
      <div class="d-flex align-items-start align-items-lg-center">
        <b-form-checkbox id="average_enable" v-model="average_enable" switch>
          Determinar uma média para aprovação
        </b-form-checkbox>
        <bp-tooltip
          text="O Gabarito será liberado somente após atingir essa média"
        >
          <span class="danger-info-circle ml-3 mb-lg-3">!</span>
        </bp-tooltip>
      </div>

      <div v-if="average_enable">
        <form-group
          v-model="form.approval_average"
          :errors="errors.approval_average"
          id="approval_average"
          type="number"
          placeholder="0 %"
          label="Média em %"
          :maxNumber="100"
          required
        />
      </div>
    </div>

    <b-form-checkbox
      id="allow_reveal_answer_keys"
      v-model="form.allow_reveal_answer_keys"
      class="mb-6"
      switch
    >
      Liberar gabarito quando atingir média
    </b-form-checkbox>

    <div class="mb-6">
      <b-form-checkbox id="retry_enable" v-model="retry_enable" switch>
        Liberar novas tentativas
      </b-form-checkbox>

      <div v-if="retry_enable">
        <form-group
          v-model="form.number_of_attempts"
          :errors="errors.number_of_attempts"
          id="number_of_attempts"
          type="number"
          placeholder="0 tentativas"
          label="Por padrão, o aluno tem apenas 1 tentativa. Caso queira permitir que ele tente responder ao questionário novamente, determine abaixo o número de tentativas extras:"
          :maxNumber="100"
          required
        />
      </div>
    </div>

    <div class="mb-6">
      <h6 class="indicator-title lg pb-0 mb-0">Liberação da avaliação</h6>
      <small class="indicator-description xs">
        Determine quando a avaliação será liberada.
      </small>
    </div>

    <b-form-checkbox
      v-if="isFinal"
      id="release_final_quiz_when_finished"
      v-model="form.release_final_quiz_when_finished"
      class="mb-6"
      switch
    >
      Liberar a avaliação após o término de todas as aulas do curso
    </b-form-checkbox>

    <div class="mb-6">
      <b-form-checkbox
        id="days_count_enable"
        v-model="days_to_relase_enable"
        switch
      >
        Liberar para quantidade de dias
      </b-form-checkbox>

      <div v-if="days_to_relase_enable">
        <form-group
          v-model="form.days_to_release"
          :errors="errors.days_to_release"
          id="days_to_release"
          type="number"
          placeholder="0 dias"
          label="Liberar após"
          :maxNumber="999"
          required
        />
      </div>
    </div>

    <div class="mb-6">
      <b-form-checkbox id="date_enable" v-model="release_at_enable" switch>
        Liberar em uma data especifica
      </b-form-checkbox>

      <div v-if="release_at_enable">
        <form-group
          v-model="form.release_at"
          :errors="errors.release_at"
          id="release_at"
          masked
          mask="##/##/####"
          placeholder="00/00/0000"
          label="Liberar no dia"
          required
        />
      </div>
    </div>

    <small class="info-wrapper w-100">
      <span class="danger-info-circle mr-2">!</span>
      Caso não tenha habilitado avaliação no processo de certificação, revise
      suas configurações em ‘Configurar Certificado’
    </small>
    <div class="d-flex align-items-center justify-content-between mt-9">
      <button class="btn btn-outline-secondary px-6" @click="$emit('cancel')">
        {{ cancel_label }}
      </button>
      <button
        class="btn btn-primary px-6"
        :disabled="isDisabled"
        @click="$emit('next')"
      >
        Próximo <i class="icon-angle-right ml-5"></i>
      </button>
    </div>
  </div>
</template>

<script>
import { BpTooltip } from "@/components/designSystem/";
import MemberAreaMixin from "@/mixins/memberArea.js";

export default {
  components: { BpTooltip },
  mixins: [MemberAreaMixin],
  props: {
    form: {
      type: Object,
      default: () => {}
    },
    isFinal: {
      type: Boolean,
      default: false
    },
    errors: {
      type: Object,
      default: () => {}
    }
  },
  computed: {
    cancel_label() {
      if (
        [
          "product-members-module-quizz-final-edit",
          "product-members-module-quizz-edit"
        ].includes(this.$route.name)
      ) {
        return "Voltar";
      }

      return "Cancelar";
    },
    isDisabled() {
      return !(
        this.form.title &&
        this.form.title.length < 64 &&
        this.form.description
      );
    }
  },
  data() {
    return {
      time_limit_enable: this.form?.time_limit_minutes > 0,
      average_enable: this.form?.approval_average > 0,
      retry_enable: this.form?.number_of_attempts > 0,
      days_to_relase_enable: this.form?.days_to_release > 0,
      release_at_enable:
        this.form.release_at && this.form.release_at.length > 0,
      loading: false
    };
  },
  watch: {
    form: {
      immediate: true,
      handler(value) {
        if (value?.time_limit_minutes > 0) {
          this.time_limit_enable = true;
        } else {
          this.form.time_limit_minutes = 0;
        }

        if (value?.approval_average > 0) {
          this.average_enable = true;
        } else {
          this.form.approval_average = 0;
        }

        if (value?.number_of_attempts > 0) {
          this.retry_enable = true;
        } else {
          this.form.number_of_attempts = 0;
        }

        if (value?.days_to_release > 0) {
          this.days_to_relase_enable = true;
          this.release_at_enable = false;
        } else {
          this.form.days_to_release = 0;
        }

        if (value?.release_at && value?.release_at.length > 0) {
          this.release_at_enable = true;
          this.days_to_relase_enable = false;
        } else {
          this.form.release_at = "";
        }
      }
    },
    release_at_enable(value) {
      if (value) {
        this.days_to_relase_enable = false;
      } else {
        this.form.release_at = "";
      }
    },
    days_to_relase_enable(value) {
      if (value) {
        this.release_at_enable = false;
      } else {
        this.form.days_to_release = 0;
      }
    },
    retry_enable(value) {
      if (!value) {
        this.form.number_of_attempts = 0;
      }
    },
    average_enable(value) {
      if (!value) {
        this.form.approval_average = 0;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.form-quizz-basic-info-component {
  a {
    text-decoration: none;
  }

  .btn {
    white-space: nowrap;
  }

  .info-wrapper {
    color: #57586be0;
    display: flex;
    align-items: center;
    justify-content: flex-start;
    @media screen and (max-width: 768px) {
      align-items: flex-start;
    }
  }

  .btn {
    .icon-angle-right {
      font-size: 20px;
    }
  }
}
</style>
